import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenantId } from './useTenantId';
import { toast } from 'sonner';

export interface ContractorPortalUser {
  id: string;
  company_id: string;
  tenant_id: string;
  auth_user_id: string | null;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  title: string | null;
  is_active: boolean;
  is_primary_contact: boolean;
  can_upload_documents: boolean;
  can_view_prequalification_status: boolean;
  invitation_sent_at: string | null;
  invitation_sent_by: string | null;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
}

export interface ContractorPortalUserFormData {
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  title?: string;
  is_primary_contact?: boolean;
  can_upload_documents?: boolean;
  can_view_prequalification_status?: boolean;
  is_active?: boolean;
}

export function useContractorPortalUsers(companyId: string) {
  const { tenantId } = useTenantId();
  const queryClient = useQueryClient();

  const { data: portalUsers, isLoading } = useQuery({
    queryKey: ['contractor-portal-users', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contractor_portal_users')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as ContractorPortalUser[];
    },
    enabled: !!companyId,
  });

  const createPortalUser = useMutation({
    mutationFn: async (formData: ContractorPortalUserFormData) => {
      const { data, error } = await supabase
        .from('contractor_portal_users')
        .insert({
          company_id: companyId,
          tenant_id: tenantId,
          ...formData,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractor-portal-users'] });
      toast.success('Portal user created successfully');
    },
    onError: (error) => {
      console.error('Error creating portal user:', error);
      toast.error('Failed to create portal user');
    },
  });

  const sendInvitation = useMutation({
    mutationFn: async (contractorUserId: string) => {
      const { data, error } = await supabase.functions.invoke('send-contractor-invitation', {
        body: {
          contractor_user_id: contractorUserId,
          tenant_id: tenantId,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractor-portal-users'] });
      toast.success('Invitation sent successfully');
    },
    onError: (error) => {
      console.error('Error sending invitation:', error);
      toast.error('Failed to send invitation');
    },
  });

  const updatePortalUser = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ContractorPortalUserFormData> }) => {
      const { error } = await supabase
        .from('contractor_portal_users')
        .update(data)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractor-portal-users'] });
      toast.success('Portal user updated successfully');
    },
    onError: (error) => {
      console.error('Error updating portal user:', error);
      toast.error('Failed to update portal user');
    },
  });

  const deletePortalUser = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('contractor_portal_users')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractor-portal-users'] });
      toast.success('Portal user deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting portal user:', error);
      toast.error('Failed to delete portal user');
    },
  });

  return {
    portalUsers,
    isLoading,
    createPortalUser: createPortalUser.mutateAsync,
    sendInvitation: sendInvitation.mutateAsync,
    updatePortalUser: updatePortalUser.mutateAsync,
    deletePortalUser: deletePortalUser.mutateAsync,
  };
}